import { 
  collection, 
  query, 
  where, 
  getDocs, 
  orderBy,
  addDoc,
  serverTimestamp 
} from 'firebase/firestore';
import { db } from '../config/firebase';

/**
 * Bulk SMS Service - Phase 1: Read-Only Filtering
 * 
 * This service provides location-based staff filtering capabilities
 * for bulk SMS communication during crisis management.
 * 
 * Phase 1: Preview and filtering only (no actual SMS sending)
 * Phase 2: Will add SMS dispatch functionality
 */

/**
 * Get available countries for an organization
 * @param {string} orgId - Organization ID
 * @returns {Array<string>} - Array of unique countries
 */
export const getAvailableCountries = async (orgId) => {
  try {
    console.log('🌍 Getting available countries for org:', orgId);
    
    const staffQuery = query(
      collection(db, 'c3_users'),
      where('orgId', '==', orgId)
    );
    
    const staffSnapshot = await getDocs(staffQuery);
    const countries = new Set();
    
    staffSnapshot.docs.forEach(doc => {
      const data = doc.data();
      if (data.country && data.country.trim()) {
        countries.add(data.country.trim());
      }
    });
    
    const sortedCountries = Array.from(countries).sort();
    console.log('🌍 Found countries:', sortedCountries);
    
    return sortedCountries;
  } catch (error) {
    console.error('❌ Error getting countries:', error);
    throw error;
  }
};

/**
 * Get available states for an organization and optional country
 * @param {string} orgId - Organization ID
 * @param {string} country - Optional country filter
 * @returns {Array<string>} - Array of unique states
 */
export const getAvailableStates = async (orgId, country = null) => {
  try {
    console.log('🏴 Getting available states for org:', orgId, 'country:', country);
    
    let staffQuery = query(
      collection(db, 'c3_users'),
      where('orgId', '==', orgId)
    );
    
    // If country is specified, add it as a filter
    if (country) {
      staffQuery = query(
        collection(db, 'c3_users'),
        where('orgId', '==', orgId),
        where('country', '==', country)
      );
    }
    
    const staffSnapshot = await getDocs(staffQuery);
    const states = new Set();
    
    staffSnapshot.docs.forEach(doc => {
      const data = doc.data();
      if (data.state && data.state.trim()) {
        states.add(data.state.trim());
      }
    });
    
    const sortedStates = Array.from(states).sort();
    console.log('🏴 Found states:', sortedStates);
    
    return sortedStates;
  } catch (error) {
    console.error('❌ Error getting states:', error);
    throw error;
  }
};

/**
 * Get available sites for an organization, optional country and state
 * @param {string} orgId - Organization ID
 * @param {string} country - Optional country filter
 * @param {string} state - Optional state filter
 * @returns {Array<Object>} - Array of sites with basic info
 */
export const getAvailableSites = async (orgId, country = null, state = null) => {
  try {
    console.log('🏢 Getting available sites for org:', orgId, 'country:', country, 'state:', state);
    
    let sitesQuery = query(
      collection(db, 'c3_sites'),
      where('orgId', '==', orgId),
      orderBy('name')
    );
    
    const sitesSnapshot = await getDocs(sitesQuery);
    let sites = sitesSnapshot.docs.map(doc => ({
      id: doc.id,
      name: doc.data().name,
      state: doc.data().state,
      country: doc.data().country,
      region: doc.data().region
    }));
    
    // Apply client-side filtering for country and state (since Firestore has compound query limitations)
    if (country) {
      sites = sites.filter(site => site.country === country);
    }
    
    if (state) {
      sites = sites.filter(site => site.state === state);
    }
    
    console.log('🏢 Found sites:', sites.length);
    
    return sites;
  } catch (error) {
    console.error('❌ Error getting sites:', error);
    throw error;
  }
};

/**
 * Get available roles for an organization
 * @param {string} orgId - Organization ID
 * @returns {Array<string>} - Array of unique roles
 */
export const getAvailableRoles = async (orgId) => {
  try {
    console.log('👥 Getting available roles for org:', orgId);
    
    const staffQuery = query(
      collection(db, 'c3_users'),
      where('orgId', '==', orgId)
    );
    
    const staffSnapshot = await getDocs(staffQuery);
    const roles = new Set();
    
    staffSnapshot.docs.forEach(doc => {
      const data = doc.data();
      if (data.role && data.role.trim()) {
        roles.add(data.role.trim());
      }
    });
    
    const sortedRoles = Array.from(roles).sort();
    console.log('👥 Found roles:', sortedRoles);
    
    return sortedRoles;
  } catch (error) {
    console.error('❌ Error getting roles:', error);
    throw error;
  }
};

/**
 * Get filtered staff based on location and role criteria
 * @param {string} orgId - Organization ID
 * @param {Object} filters - Filter criteria
 * @param {string} filters.country - Country filter
 * @param {string} filters.state - State filter
 * @param {string} filters.siteName - Site name filter
 * @param {string} filters.role - Role filter
 * @returns {Object} - Filtered staff results with count and preview
 */
export const getFilteredStaff = async (orgId, filters = {}) => {
  try {
    console.log('🎯 Filtering staff for org:', orgId, 'filters:', filters);
    
    // Start with basic org query
    let staffQuery = query(
      collection(db, 'c3_users'),
      where('orgId', '==', orgId),
      orderBy('lastName')
    );
    
    const staffSnapshot = await getDocs(staffQuery);
    let staffData = staffSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    // Apply client-side filtering (Firestore compound query limitations)
    let filteredStaff = staffData;
    
    if (filters.country) {
      filteredStaff = filteredStaff.filter(staff => staff.country === filters.country);
    }
    
    if (filters.state) {
      filteredStaff = filteredStaff.filter(staff => staff.state === filters.state);
    }
    
    if (filters.siteName) {
      filteredStaff = filteredStaff.filter(staff => staff.siteName === filters.siteName);
    }
    
    if (filters.role) {
      filteredStaff = filteredStaff.filter(staff => staff.role === filters.role);
    }
    
    // Only include staff with valid mobile numbers
    const staffWithMobile = filteredStaff.filter(staff => 
      staff.mobile && staff.mobile.trim() && staff.mobile.trim() !== ''
    );
    
    const results = {
      totalCount: staffWithMobile.length,
      staff: staffWithMobile,
      preview: staffWithMobile.slice(0, 10), // First 10 for preview
      filters: filters,
      generatedAt: new Date().toISOString()
    };
    
    console.log('🎯 Filtered results:', {
      totalStaff: staffData.length,
      filteredCount: filteredStaff.length,
      withMobileCount: staffWithMobile.length,
      appliedFilters: filters
    });
    
    return results;
  } catch (error) {
    console.error('❌ Error filtering staff:', error);
    throw error;
  }
};

/**
 * Log filter usage for analytics (Phase 1)
 * @param {string} orgId - Organization ID
 * @param {Object} filters - Applied filters
 * @param {number} resultCount - Number of staff matched
 * @param {string} userId - User who performed the filter
 */
export const logFilterUsage = async (orgId, filters, resultCount, userId) => {
  try {
    const logEntry = {
      orgId,
      filters,
      resultCount,
      userId,
      action: 'bulk_sms_filter_preview',
      timestamp: serverTimestamp(),
      phase: 'phase1_readonly'
    };
    
    await addDoc(collection(db, 'bulk_sms_filter_logs'), logEntry);
    console.log('📊 Filter usage logged:', logEntry);
  } catch (error) {
    console.error('❌ Error logging filter usage:', error);
    // Don't throw - logging failure shouldn't break the main functionality
  }
};

/**
 * Validate filter parameters
 * @param {Object} filters - Filter object to validate
 * @returns {Object} - Validation result
 */
export const validateFilters = (filters) => {
  const errors = [];
  const warnings = [];
  
  // At least one filter should be applied
  const hasAnyFilter = filters.country || filters.state || filters.siteName || filters.role;
  
  if (!hasAnyFilter) {
    warnings.push('No filters applied - this will target all staff in the organization');
  }
  
  // Validate filter combinations
  if (filters.siteName && !filters.state) {
    warnings.push('Site specified without state - consider adding state filter for clarity');
  }
  
  if (filters.state && !filters.country) {
    warnings.push('State specified without country - consider adding country filter for clarity');
  }
  
  return {
    isValid: errors.length === 0,
    errors,
    warnings,
    hasFilters: hasAnyFilter
  };
};

/**
 * Phase 1 Export: Generate summary report of filtering capabilities
 * @param {string} orgId - Organization ID
 * @returns {Object} - Summary report
 */
export const generateFilteringSummary = async (orgId) => {
  try {
    console.log('📋 Generating filtering summary for org:', orgId);
    
    const [countries, states, sites, roles, allStaff] = await Promise.all([
      getAvailableCountries(orgId),
      getAvailableStates(orgId),
      getAvailableSites(orgId),
      getAvailableRoles(orgId),
      getFilteredStaff(orgId, {}) // No filters = all staff
    ]);
    
    const summary = {
      orgId,
      generatedAt: new Date().toISOString(),
      phase: 'phase1_readonly',
      totalStaff: allStaff.totalCount,
      breakdown: {
        countries: countries.length,
        states: states.length,
        sites: sites.length,
        roles: roles.length
      },
      availableOptions: {
        countries,
        states: states.slice(0, 10), // Limit for display
        roles
      },
      capabilities: [
        'Filter by Country',
        'Filter by State',
        'Filter by Site',
        'Filter by Role',
        'Combination Filters',
        'Real-time Preview',
        'Mobile Number Validation'
      ],
      phase2_features: [
        'SMS Dispatch',
        'Batch Processing',
        'Delivery Tracking',
        'Cost Management',
        'Message Templates'
      ]
    };
    
    console.log('📋 Summary generated:', summary);
    return summary;
  } catch (error) {
    console.error('❌ Error generating summary:', error);
    throw error;
  }
};

export default {
  getAvailableCountries,
  getAvailableStates,
  getAvailableSites,
  getAvailableRoles,
  getFilteredStaff,
  logFilterUsage,
  validateFilters,
  generateFilteringSummary
}; 